"use client";

import Link from "next/link";
import Image from "next/image";
import {memo} from "react";
import {useTranslation} from "react-i18next";
import {LocalizationKeys} from "@/lib/all-localization-keys";
import {OriLogo} from "@/lib/images";
import {SidebarHistory} from "@/components/sidebar-history";
import {SidebarUserNav} from "@/components/sidebar-user-nav";
import {Skeleton} from "@/components/ui/skeleton";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar";
import {ClientSessionData} from "@/app/auth/session-types";

const AppSidebarComponent = ({user, isLoading}: {
  user: ClientSessionData['user'] | undefined,
  isLoading?: boolean
}) => {
  const {t} = useTranslation();
  const {setOpenMobile} = useSidebar();

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarMenu>
          <div className="flex flex-col gap-2">
            <Link
              className="flex flex-row items-center gap-2 py-2"
              href="/"
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <div className="relative h-15 w-full">
                <Image
                  src={OriLogo.light}
                  alt={t(LocalizationKeys.APP_PRODUCT_NAME)}
                  fill
                  className="object-contain object-left dark:hidden"
                  priority
                />
                <Image
                  src={OriLogo.dark}
                  alt={t(LocalizationKeys.APP_PRODUCT_NAME)}
                  fill
                  className="object-contain object-left hidden dark:block"
                  priority
                />
              </div>
            </Link>
          </div>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {
          isLoading
            ? (
              <div className="flex flex-col gap-2 p-2">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full"/>
                ))}
              </div>
            )
            : <SidebarHistory user={user}/>
        }
      </SidebarContent>
      <SidebarFooter>
        {
          isLoading
            ? <Skeleton className="h-12 w-full mx-2"/>
            : user
              ? <SidebarUserNav user={user}/>
              : null
        }
      </SidebarFooter>
    </Sidebar>
  );
};

export const AppSidebar = memo(
  AppSidebarComponent,
  (prevProps, nextProps) =>
    prevProps.user?.authId === nextProps.user?.authId &&
    prevProps.isLoading === nextProps.isLoading
);
